import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { embedService } from '../../../services/embedService';

const SOCIAL_NETWORKS = [
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    icon: 'MessageCircle',
    color: 'text-green-600',
    getUrl: (url) => `https://wa.me/?text=${encodeURIComponent(url)}`
  },
  {
    key: 'facebook',
    label: 'Facebook',
    icon: 'Facebook',
    color: 'text-blue-600',
    getUrl: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
  },
  {
    key: 'twitter',
    label: 'X (Twitter)',
    icon: 'Twitter',
    color: 'text-foreground',
    getUrl: (url) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`
  },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    icon: 'Linkedin',
    color: 'text-blue-700',
    getUrl: (url) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
  },
  {
    key: 'email',
    label: 'Email',
    icon: 'Mail',
    color: 'text-muted-foreground',
    getUrl: (url) => `mailto:?subject=${encodeURIComponent('Mira este catálogo')}&body=${encodeURIComponent(url)}`
  }
];

const TestingTools = ({ catalogId }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const shareRef = useRef(null);

  const embedUrl = embedService?.getCatalogEmbedUrl(catalogId);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (shareRef.current && !shareRef.current.contains(e.target)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCopyUrl = async () => {
    const success = await embedService?.copyEmbedCode(embedUrl);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenInNewTab = () => {
    window.open(embedUrl, '_blank', 'noopener,noreferrer');
  };

  const handleShareNetwork = (network) => {
    window.open(network.getUrl(embedUrl), '_blank', 'noopener,noreferrer');
    setShowShareMenu(false);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
      <div className="flex items-center mb-4 md:mb-6">
        <Icon name="TestTube" size={20} className="text-accent mr-2 md:mr-3" />
        <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
          {t('embedSettings.testing.title')}
        </h2>
      </div>

      <div className="space-y-4 md:space-y-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            {t('embedSettings.testing.previewUrlLabel')}
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={embedUrl}
              readOnly
              className="flex-1 px-3 py-2 bg-muted border border-border rounded-md text-sm text-foreground font-mono"
            />
            <Button
              variant="outline"
              size="sm"
              iconName={copied ? 'Check' : 'Copy'}
              onClick={handleCopyUrl}
            >
              {copied ? t('embedSettings.testing.copied') : t('embedSettings.testing.copy')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="ExternalLink"
              onClick={handleOpenInNewTab}
            >
              {t('embedSettings.testing.open')}
            </Button>
            <div className="relative" ref={shareRef}>
              <Button
                variant="outline"
                size="sm"
                iconName="Share2"
                onClick={() => setShowShareMenu(!showShareMenu)}
              >
                {t('embedSettings.testing.share')}
              </Button>
              {showShareMenu && (
                <div className="absolute right-0 top-full mt-1 bg-popover border border-border rounded-lg shadow-elevation-lg z-50 overflow-hidden min-w-[160px]">
                  {SOCIAL_NETWORKS.map((network) => (
                    <button
                      key={network.key}
                      onClick={() => handleShareNetwork(network)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors text-left"
                    >
                      <Icon name={network.icon} size={16} className={network.color} />
                      {network.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {t('embedSettings.testing.previewUrlDesc')}
          </p>
        </div>

        <div className="border-t border-border pt-4 md:pt-6">
          <h3 className="text-base font-medium text-foreground mb-3">
            {t('embedSettings.testing.checklistTitle')}
          </h3>
          <div className="space-y-2 text-sm text-muted-foreground">
            <div className="flex items-start gap-2">
              <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
              <span>{t('embedSettings.testing.check1')}</span>
            </div>
            <div className="flex items-start gap-2">
              <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
              <span>{t('embedSettings.testing.check2')}</span>
            </div>
            <div className="flex items-start gap-2">
              <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
              <span>{t('embedSettings.testing.check3')}</span>
            </div>
            <div className="flex items-start gap-2">
              <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
              <span>{t('embedSettings.testing.check4')}</span>
            </div>
            <div className="flex items-start gap-2">
              <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
              <span>{t('embedSettings.testing.check5')}</span>
            </div>
          </div>
        </div>

        <div className="bg-muted/50 rounded-md p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={16} className="text-accent mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-foreground mb-1">{t('embedSettings.testing.externalTitle')}</p>
              <p className="text-muted-foreground">
                {t('embedSettings.testing.externalDesc')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestingTools;